/**
 * XPTable — experience curve and XP source calculations.
 *
 * XP curve: quadratic with floor — 50 + 5 × level².
 *   Lv1→2: 55,  Lv5→6: 175,  Lv10→11: 550,  Lv20→21: 2050
 * Word XP uses the same multipliers as damage: length + adjacency.
 * Victory XP scales with player level.
 */

import type { EnemyTier } from "../EnemyCharacter";
/**
 * XP length multiplier — steeper than damage to reward longer words.
 *
 *   2–3 letters → 1.0×
 *   4 letters   → 1.3×
 *   5 letters   → 1.6×
 *   6 letters   → 2.0×
 *   7 letters   → 2.4×
 *   8+ letters  → 3.0×
 */
function xpLengthMultiplier(wordLength: number): number {
  if (wordLength <= 3) return 1;
  return Math.min(1 + (wordLength - 3) * 0.4, 3);
}

/**
 * XP required to advance from `level` to `level + 1`.
 * Quadratic with floor: 55, 70, 95, 130, 175, 230, 295, 370, 455, 550...
 */
export function xpForLevel(level: number): number {
  return 50 + 5 * level * level;
}

/** No level cap — players can keep leveling. */
export const MAX_LEVEL = Infinity;

// --- XP source calculations ---

const RARE_LETTERS = new Set(["Q", "Z", "X", "J"]);

/**
 * XP earned from submitting a valid word.
 * Formula: (wordScore + rareBonus) × lengthMult × adjMult
 *
 * @param word      - The submitted word
 * @param wordScore - Base score from LetterScoring
 * @param adjMult   - Adjacency chain multiplier (1.0 if no chain)
 */
export function wordXP(word: string, wordScore: number, adjMult: number = 1): number {
  let xp = wordScore;

  // Rare letter bonus: +1 per Q/Z/X/J used
  for (const ch of word) {
    if (RARE_LETTERS.has(ch.toUpperCase())) {
      xp += 1;
    }
  }

  // XP uses a steeper length multiplier than damage
  const lenMult = xpLengthMultiplier(word.length);
  // Combo bonus: adjacency + length together gives extra XP
  const comboBonus = adjMult > 1 && lenMult > 1 ? 1 + (adjMult - 1) * (lenMult - 1) : 1;
  xp = Math.floor(xp * lenMult * adjMult * comboBonus);

  return xp;
}

/**
 * XP earned for killing a single enemy. Awarded immediately on death.
 * Base: 5 normal, 8 elite, 12 boss. Plus enemyLevel × 2.
 * One-shot bonus: +50% if enemy was at full HP.
 */
export function enemyKillXP(tier: EnemyTier, enemyLevel: number, oneshot: boolean): number {
  let base: number;
  switch (tier) {
    case "boss": base = 12; break;
    case "elite": base = 8; break;
    default: base = 5;
  }
  const killXp = base + enemyLevel * 2;
  return oneshot ? Math.ceil(killXp * 1.5) : killXp;
}

/**
 * Small completion bonus for winning the battle (all enemies dead).
 * Flat: 5 normal, 8 elite, 12 boss.
 */
export function battleVictoryXP(tier: EnemyTier): number {
  switch (tier) {
    case "boss": return 12;
    case "elite": return 8;
    default: return 5;
  }
}
